// src/pages/CoiProvidersPage.jsx
// Provider-identity matching review for uploaded COI questionnaire data.
// Structural mirror of MyVendorsPage.jsx's vendor-matching table, adapted
// for NPI identity instead of company identity — confirm/reject a
// suggested NPPES match, or manually enter/search an NPI when the
// algorithm found nothing usable.

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useUser, useAuth } from "@clerk/react";
import { useSubscription } from "../context/SubscriptionContext";
import { authedFetch } from "../utils/apiClient";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import CoiDisclosureThresholdsPopup from "../components/CoiDisclosureThresholdsPopup";

const COI_COL_MAP = {
  npi: ["npi", "npi number", "npi_number", "national provider identifier"],
  first_name: ["first name", "first_name", "firstname", "provider first name"],
  last_name: ["last name", "last_name", "lastname", "provider last name"],
  state: ["state", "practice state", "provider state"],
  company: ["company", "company name", "business entity", "company / business entity", "company/business entity", "entity"],
  relationship_type: ["relationship type", "relationship_type", "type of relationship", "nature of relationship"],
  notes: ["description", "notes", "description / notes", "description/notes", "details"],
  involves_family: ["involves family", "family", "family member", "involves immediate family"],
  family_relationship: ["family relationship", "relationship to family member"],
};

function normalizeCoiHeaders(row) {
  const normalized = {};
  for (const [key, val] of Object.entries(row)) {
    const lk = key.toLowerCase().trim();
    for (const [field, aliases] of Object.entries(COI_COL_MAP)) {
      if (aliases.includes(lk)) { normalized[field] = val; break; }
    }
  }
  return normalized;
}

function parseCoiFile(file) {
  return new Promise((resolve, reject) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv") {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: (results) => resolve(results.data.map(normalizeCoiHeaders)),
        error: reject,
      });
    } else if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(ws, { defval: "" }).map(normalizeCoiHeaders));
        } catch (err) { reject(err); }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error("Unsupported file type. Please upload a CSV or Excel file."));
    }
  });
}

// Only rows with Involves Family = Yes are treated as a family member's
// conflict; every other row counts as the provider's own disclosure, which can
// suppress a real Disclosure Gap. These rules catch rows that look like they
// were meant to be family rows. The yes test mirrors api/upload-coi.js. Parent,
// child(ren), and family are left out on purpose: "parent company," "Children's
// Hospital," and "family medicine" would drown the real hits.
const FAMILY_TERMS = /\b(wife|husband|spouse|son|daughter|mother|father|brother|sister|siblings?|in-laws?)\b/i;
const FLAG_YES = /^(y|yes|true|1)$/i;
const FLAG_NO_OR_BLANK = /^(n|no|false|0)?$/i;

function findFamilyFlagIssues(rows) {
  const issues = [];
  rows.forEach((r, i) => {
    const flag = String(r.involves_family || "").trim();
    if (FLAG_YES.test(flag)) return;
    const row = i + 2; // the header is row 1, matching the server's numbering
    const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "(no name)";
    const company = String(r.company || "").trim();
    if (!FLAG_NO_OR_BLANK.test(flag)) {
      issues.push({ row, name, company, reason: `Involves Family is "${flag}", which is read as No; use Yes or No` });
      return;
    }
    const hit = `${r.notes || ""} ${r.relationship_type || ""}`.match(FAMILY_TERMS);
    if (hit) {
      issues.push({ row, name, company, reason: `notes mention "${hit[0].toLowerCase()}" but Involves Family isn't Yes` });
      return;
    }
    const rel = String(r.family_relationship || "").trim();
    if (rel) issues.push({ row, name, company, reason: `Family Relationship is "${rel}" but Involves Family isn't Yes` });
  });
  return issues;
}

function CoiUploadPanel({ userId, getToken, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [listName, setListName] = useState("");
  const [parseError, setParseError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const familyIssues = useMemo(() => findFamilyFlagIssues(rows), [rows]);

  async function handleFile(f) {
    if (!f) return;
    setFile(f); setParseError(null); setResult(null);
    try {
      const parsed = await parseCoiFile(f);
      const usable = parsed.filter((r) => r.first_name || r.last_name || r.company);
      if (!usable.length) {
        setParseError("No usable rows found — check that your file has First Name, Last Name, and Company columns.");
        return;
      }
      setRows(usable);
      if (!listName) setListName(f.name.replace(/\.(csv|xlsx|xls)$/i, ""));
    } catch (err) {
      setParseError(err.message || "Failed to parse file.");
    }
  }

  async function submitUpload() {
    if (!rows.length || !listName.trim()) return;
    setUploading(true); setResult(null);
    try {
      const res = await authedFetch(getToken, "/api/upload-coi", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, listName: listName.trim(), rows }),
      });
      const data = await res.json();
      if (!res.ok) { setParseError(data.error || "Upload failed."); return; }
      setResult(data);
      onUploaded?.(data.listId);
    } catch {
      setParseError("Network error. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  function reset() {
    setFile(null); setRows([]); setListName(""); setParseError(null); setResult(null);
  }

  return (
    <div className="card space-y-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between">
        <span className="text-white/70 text-sm font-semibold">+ Upload COI List</span>
        <span className="text-white/30 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="space-y-4 pt-2 border-t border-white/5">
          {!result && (
            <>
              <div>
                <label className="text-white/50 text-xs mb-1 block">List Name</label>
                <input
                  type="text" value={listName} onChange={(e) => setListName(e.target.value)}
                  placeholder="2026 Physician COI Survey"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
                />
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${file ? "border-green-500/30 bg-green-500/5" : "border-white/15 hover:border-white/30"}`}
              >
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
                {file ? (
                  <p className="text-green-400 text-sm">{file.name} — {rows.length} row{rows.length !== 1 ? "s" : ""} parsed</p>
                ) : (
                  <p className="text-white/60 text-sm">Drop a CSV or Excel file here, or click to browse</p>
                )}
              </div>

              <details className="text-xs text-white/40">
                <summary className="cursor-pointer hover:text-white/60">📋 Expected columns</summary>
                <table className="mt-2 w-full border-collapse">
                  <tbody>
                    {[
                      ["First Name", "Required"], ["Last Name", "Required"],
                      ["Company / Business Entity", "Required — one company per row. Enter 'None' if the provider disclosed nothing"],
                      ["NPI", "Optional"], ["State", "Recommended if no NPI"],
                      ["Relationship Type", "Optional — e.g. Ownership, Consulting, Employment"],
                      ["Description / Notes", "Optional"], ["Involves Family", "Optional — Yes when the conflict belongs to a family member; blank or No means the provider's own"],
                      ["Family Relationship", "Optional"],
                    ].map(([col, desc]) => (
                      <tr key={col} className="border-t border-white/5 first:border-t-0">
                        <td className="font-mono text-white/60 py-1 pr-4 align-top whitespace-nowrap">{col}</td>
                        <td className="py-1 text-white/40">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>

              {parseError && <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">✗ {parseError}</p>}

              {familyIssues.length > 0 && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2.5 space-y-1.5">
                  <p className="text-amber-400/90 text-xs font-medium">
                    ⚠ {familyIssues.length} row{familyIssues.length !== 1 ? "s" : ""} may be recorded as the provider's own disclosure by mistake
                  </p>
                  <p className="text-white/40 text-xs">
                    Only rows with Involves Family = Yes are treated as a family member's conflict. Any other row counts as the provider disclosing their own relationship, which can hide a real Disclosure Gap. Row numbers count the header as row 1. You can still upload; this is a heads-up, not a block.
                  </p>
                  <ul className="text-white/50 text-xs space-y-0.5">
                    {familyIssues.slice(0, 8).map((issue) => (
                      <li key={issue.row}>Row {issue.row} · {issue.name}{issue.company ? ` · ${issue.company}` : ""}: {issue.reason}</li>
                    ))}
                  </ul>
                  {familyIssues.length > 8 && <p className="text-white/30 text-xs">+ {familyIssues.length - 8} more</p>}
                </div>
              )}

              {rows.length > 0 && (
                <div className="flex gap-3">
                  <button onClick={submitUpload} disabled={uploading || !listName.trim()} className="btn-primary text-sm px-5 py-2 disabled:opacity-50">
                    {uploading ? "Uploading…" : `Upload ${rows.length} row${rows.length !== 1 ? "s" : ""}`}
                  </button>
                  <button onClick={reset} className="text-white/40 hover:text-white/70 text-sm">Clear</button>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3 space-y-1">
              <p className="text-green-400 text-sm font-semibold">✓ Upload complete</p>
              <p className="text-white/50 text-xs">
                {result.providersInserted} provider{result.providersInserted !== 1 ? "s" : ""} · {result.disclosuresInserted} disclosure{result.disclosuresInserted !== 1 ? "s" : ""}
                {result.formatErrors?.length > 0 && ` · ${result.formatErrors.length} row${result.formatErrors.length !== 1 ? "s" : ""} skipped`}
              </p>
              {result.matchSummary && !result.matchSummary.error && (
                <p className="text-white/40 text-xs">
                  Matching: {result.matchSummary.confirmed_by_npi || 0} by NPI · {result.matchSummary.confirmed_by_user || 0} reused ·{" "}
                  {result.matchSummary.suggested || 0} suggested · {result.matchSummary.ambiguous || 0} ambiguous ·{" "}
                  {result.matchSummary.needs_review || 0} need review · {result.matchSummary.unmatched || 0} unmatched
                </p>
              )}
              <button onClick={reset} className="text-white/40 hover:text-white/60 text-xs underline">Upload another file</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

function MatchStatusBadge({ status }) {
  const styles = {
    unmatched: "bg-white/5 border-white/10 text-white/40",
    needs_review: "bg-amber-500/10 border-amber-500/20 text-amber-400",
    suggested: "bg-gold-500/10 border-gold-500/20 text-gold-400",
    ambiguous: "bg-orange-500/10 border-orange-500/20 text-orange-400",
    confirmed_by_npi: "bg-green-500/10 border-green-500/20 text-green-400",
    confirmed_by_user: "bg-teal-500/10 border-teal-500/20 text-teal-400",
    rejected: "bg-red-500/10 border-red-500/20 text-red-400",
  };
  const labels = {
    unmatched: "Unmatched", needs_review: "Needs Review", suggested: "Suggested", ambiguous: "Ambiguous",
    confirmed_by_npi: "Confirmed (NPI)", confirmed_by_user: "Confirmed", rejected: "Rejected",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[status] || styles.unmatched}`}>
      {labels[status] || "Unmatched"}
    </span>
  );
}

const LOW_CONFIDENCE_THRESHOLD = 80;

function ConfidenceLabel({ confidence, method }) {
  if (typeof confidence !== "number") return null;
  const low = confidence < LOW_CONFIDENCE_THRESHOLD;
  const methodLabel = {
    fuzzy_suggested: "name match", mapping_reuse: "reuse", npi_provided: "NPI provided", manual: "manual",
    exact: "exact match", exact_nospace: "exact match", exact_abbrev: "exact match", alias: "alias", fuzzy: "name match",
  }[method] || method;
  return (
    <p className="text-white/30 text-[11px] flex items-center gap-1.5 justify-end">
      <span>{confidence}% · {methodLabel}</span>
      {low && <span className="text-amber-400/80">⚠ Low confidence</span>}
    </p>
  );
}

function PaginationControls({ page, total, pageSize, onPageChange }) {
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  return (
    <div className="flex items-center gap-2 text-xs text-white/40">
      <button onClick={() => onPageChange(0)} disabled={page === 0} className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30">«</button>
      <button onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0} className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30">←</button>
      <span>Page {page + 1} of {lastPage + 1}</span>
      <button onClick={() => onPageChange(Math.min(lastPage, page + 1))} disabled={page >= lastPage} className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30">→</button>
      <button onClick={() => onPageChange(lastPage)} disabled={page >= lastPage} className="px-2 py-1 rounded border border-white/10 hover:border-white/30 disabled:opacity-30">»</button>
    </div>
  );
}

function NpiMatchBox({ userId, listId, providerRowId, getToken, onAssign, onCancel }) {
  const [npiInput, setNpiInput] = useState("");
  const [lookup, setLookup] = useState(null); // null | "loading" | "error" | { found, firstName, lastName, state }

  useEffect(() => {
    const npi = npiInput.trim();
    if (!/^\d{10}$/.test(npi)) {
      setLookup(null);
      return;
    }
    setLookup("loading");
    const t = setTimeout(async () => {
      try {
        const res = await authedFetch(getToken, "/api/match-providers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "lookupNpi", userId, npi, listId, providerRowId }),
        });
        const data = await res.json();
        setLookup(res.ok ? data : "error");
      } catch {
        setLookup("error");
      }
    }, 300);
    return () => clearTimeout(t);
  }, [npiInput, userId, listId, providerRowId]);

  const npi = npiInput.trim();

  return (
    <div className="bg-navy-900 border border-gold-500/20 rounded-lg p-3 mt-2 space-y-3">
      <div>
        <label className="text-white/40 text-xs mb-1 block">Enter NPI</label>
        <input
          autoFocus
          type="text"
          value={npiInput}
          onChange={(e) => setNpiInput(e.target.value)}
          placeholder="10-digit NPI"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-gold-500/50"
        />
      </div>

      {lookup === "loading" && <p className="text-white/30 text-xs">Looking up…</p>}
      {lookup === "error" && <p className="text-red-400 text-xs">Lookup failed. Please try again.</p>}
      {lookup && lookup !== "loading" && lookup !== "error" && !lookup.found && (
        <p className="text-red-400 text-xs">No NPPES record found for this NPI — double-check the number before proceeding.</p>
      )}
      {lookup && lookup.found && (
        <div className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 space-y-2">
          <p className="text-white text-sm">{lookup.firstName} {lookup.lastName} — {lookup.state}</p>
          {lookup.specialty && <p className="text-white/40 text-xs">{lookup.specialty}</p>}
          <p className="text-white/30 text-xs">NPI {npi}</p>
          {lookup.alreadyExists && (
            <p className="text-amber-400/80 text-xs">
              ⚠ Already matched elsewhere in this list, as "{lookup.alreadyExists.rawName || "an unnamed row"}"
            </p>
          )}
          <button
            onClick={() => onAssign(npi)}
            className="text-xs text-green-400 hover:text-green-300 border border-green-500/20 rounded-full px-3 py-1"
          >
            Confirm this is the correct provider
          </button>
        </div>
      )}

      <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs">Cancel</button>
    </div>
  );
}

function ProviderTable({ providers, onConfirm, onReject, getToken, userId, listId }) {
  const [openMatchId, setOpenMatchId] = useState(null);

  return (
    <div className="rounded-xl border border-white/10 divide-y divide-white/5">
      {providers.map((p) => (
        <div key={p.id} className="px-3 py-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-white/80 text-sm font-medium truncate">
                {[p.raw_first_name, p.raw_last_name].filter(Boolean).join(" ") || "—"}
              </p>
              <p className="text-white/30 text-xs">
                {[p.raw_state, p.raw_npi_text ? `NPI on file: ${p.raw_npi_text}` : null].filter(Boolean).join(" · ") || "—"}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {p.npi_match_status === "ambiguous" && (
                <div className="flex flex-col items-end gap-1.5">
                  <p className="text-white/40 text-[11px]">Multiple people confirmed under this name before — pick one, or search fresh:</p>
                  <div className="flex flex-wrap gap-1.5 justify-end max-w-md">
                    {(p.ambiguous_candidates || []).map((c) => (
                      <button
                        key={c.npi}
                        onClick={() => onConfirm(p.id, c.npi)}
                        className="text-left border border-white/15 hover:border-gold-500/40 rounded-lg px-2.5 py-1.5 hover:bg-white/5 transition-colors"
                      >
                        <p className="text-xs text-white/70">
                          {c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : `NPI ${c.npi}`}{c.state ? ` — ${c.state}` : ""}
                        </p>
                        {c.specialty && <p className="text-white/30 text-[11px]">{c.specialty}</p>}
                        <p className="text-white/30 text-[11px]">
                          NPI {c.npi}{c.confirmedAt && ` · last confirmed ${new Date(c.confirmedAt).toLocaleDateString()}`}
                        </p>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setOpenMatchId(openMatchId === p.id ? null : p.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    None of these — Search
                  </button>
                </div>
              )}

              {(p.npi_match_status === "unmatched" || p.npi_match_status === "needs_review") && (
                <>
                  <MatchStatusBadge status={p.npi_match_status} />
                  <button
                    onClick={() => setOpenMatchId(openMatchId === p.id ? null : p.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Match
                  </button>
                </>
              )}

              {p.npi_match_status === "suggested" && (
                <>
                  <div className="text-right">
                    <p className="text-white/70 text-xs">{p.matched_provider_name || `NPI ${p.matched_npi}`}</p>
                    {p.matched_provider_name && <p className="text-white/30 text-[11px]">NPI {p.matched_npi}</p>}
                    <ConfidenceLabel confidence={p.npi_match_confidence} method={p.npi_match_method} />
                  </div>
                  <button
                    onClick={() => onConfirm(p.id, p.matched_npi)}
                    className="text-xs text-green-400 hover:text-green-300 border border-green-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setOpenMatchId(openMatchId === p.id ? null : p.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Change
                  </button>
                  <button
                    onClick={() => onReject(p.id)}
                    className="text-xs text-red-400/70 hover:text-red-400 border border-red-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Reject
                  </button>
                </>
              )}

              {p.npi_match_status === "rejected" && (
                <>
                  <MatchStatusBadge status={p.npi_match_status} />
                  <button
                    onClick={() => setOpenMatchId(openMatchId === p.id ? null : p.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    Match
                  </button>
                </>
              )}

              {(p.npi_match_status === "confirmed_by_npi" || p.npi_match_status === "confirmed_by_user") && (
                <>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <p className="text-white/70 text-xs">{p.matched_provider_name || `NPI ${p.matched_npi}`}</p>
                      <MatchStatusBadge status={p.npi_match_status} />
                    </div>
                    {p.matched_provider_name && <p className="text-white/30 text-[11px] text-right">NPI {p.matched_npi}</p>}
                    {p.confirmed_by_email && (
                      <p className="text-white/20 text-[11px]">confirmed by {p.confirmed_by_email}</p>
                    )}
                    {p.duplicate_of && p.duplicate_of.length > 0 && (
                      <p className="text-amber-400/80 text-[11px] text-right">
                        ⚠ Also appears as {p.duplicate_of.map((d) => d.rawName || "an unnamed row").join(", ")}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setOpenMatchId(openMatchId === p.id ? null : p.id)}
                    className="text-xs text-white/30 hover:text-white/60"
                  >
                    Change
                  </button>
                </>
              )}
            </div>
          </div>

          {openMatchId === p.id && (
            <NpiMatchBox
              userId={userId}
              listId={listId}
              providerRowId={p.id}
              getToken={getToken}
              onAssign={(npi) => { onConfirm(p.id, npi); setOpenMatchId(null); }}
              onCancel={() => setOpenMatchId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function DisclosureStatusBadge({ status }) {
  const styles = {
    unmatched: "bg-white/5 border-white/10 text-white/40",
    suggested: "bg-gold-500/10 border-gold-500/20 text-gold-400",
    ambiguous: "bg-orange-500/10 border-orange-500/20 text-orange-400",
    confirmed: "bg-green-500/10 border-green-500/20 text-green-400",
    not_applicable: "bg-white/5 border-white/15 text-white/50",
    rejected: "bg-red-500/10 border-red-500/20 text-red-400",
  };
  const labels = {
    unmatched: "Unmatched", suggested: "Suggested", ambiguous: "Ambiguous",
    confirmed: "Confirmed", not_applicable: "Not Applicable", rejected: "Rejected",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${styles[status] || styles.unmatched}`}>
      {labels[status] || "Unmatched"}
    </span>
  );
}

function CompanySearchBox({ userId, getToken, onAssign, onCancel }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await authedFetch(getToken, "/api/match-coi-disclosures", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "searchCompanies", userId, query: query.trim() }),
        });
        const data = await res.json();
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, userId]);

  return (
    <div className="bg-navy-900 border border-gold-500/20 rounded-lg p-3 mt-2 space-y-2">
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search company name..."
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-gold-500/50"
      />
      {searching && <p className="text-white/30 text-xs">Searching…</p>}
      {!searching && results.length > 0 && (
        <div className="max-h-40 overflow-y-auto space-y-1">
          {results.map((r) => (
            <button
              key={r.companyId}
              onClick={() => onAssign(r.companyId, r.companyName)}
              className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-white/5 text-xs text-white/70 hover:text-white transition-colors"
            >
              <span>{r.companyName}</span>
              <span className="text-white/30">{r.confidence}%</span>
            </button>
          ))}
        </div>
      )}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="text-white/30 text-xs">No matches found.</p>
      )}
      <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs">Cancel</button>
    </div>
  );
}

function DisclosureTable({ disclosures, onConfirm, onReject, onNotApplicable, onUndoConfirm, getToken, userId }) {
  const [openMatchId, setOpenMatchId] = useState(null);

  return (
    <div className="rounded-xl border border-white/10 divide-y divide-white/5">
      {disclosures.map((d) => (
        <div key={d.id} className="px-3 py-2.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-white/80 text-sm font-medium truncate">{d.raw_company_text}</p>
              <p className="text-white/30 text-xs truncate">
                {d.providerName || d.providerRawName || "—"}
                {d.relationship_type ? ` · ${d.relationship_type}` : ""}
                {d.involves_family ? ` · involves family${d.family_relationship ? ` (${d.family_relationship})` : ""}` : ""}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {d.match_status === "ambiguous" && (
                <div className="flex flex-col items-end gap-1.5">
                  <p className="text-white/40 text-[11px]">Multiple possible companies — pick one, or search fresh:</p>
                  <div className="flex flex-wrap gap-1.5 justify-end max-w-md">
                    {(d.ambiguous_candidates || []).map((c) => (
                      <button
                        key={c.companyId}
                        onClick={() => onConfirm(d.id, c.companyId, c.companyName)}
                        className="text-left border border-white/15 hover:border-gold-500/40 rounded-lg px-2.5 py-1.5 hover:bg-white/5 transition-colors"
                      >
                        <p className="text-xs text-white/70">{c.companyName}{c.isPrimary ? " (primary)" : ""}</p>
                        {typeof c.confidence === "number" && <p className="text-white/30 text-[11px]">{c.confidence}%</p>}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setOpenMatchId(openMatchId === d.id ? null : d.id)}
                    className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5"
                  >
                    None of these — Search
                  </button>
                </div>
              )}

              {d.match_status === "unmatched" && (
                <>
                  <DisclosureStatusBadge status={d.match_status} />
                  <button onClick={() => onNotApplicable(d.id)} className="text-xs text-white/40 hover:text-white/70 border border-white/15 rounded-full px-2.5 py-0.5">
                    Not Applicable
                  </button>
                  <button onClick={() => setOpenMatchId(openMatchId === d.id ? null : d.id)} className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5">
                    Match
                  </button>
                </>
              )}

              {d.match_status === "suggested" && (
                <>
                  <div className="text-right">
                    <p className="text-white/70 text-xs">{d.matched_company_name}</p>
                    <ConfidenceLabel confidence={d.match_confidence} method={d.match_method} />
                  </div>
                  <button onClick={() => onConfirm(d.id, d.matched_company_id, d.matched_company_name)} className="text-xs text-green-400 hover:text-green-300 border border-green-500/20 rounded-full px-2.5 py-0.5">
                    Confirm
                  </button>
                  <button onClick={() => setOpenMatchId(openMatchId === d.id ? null : d.id)} className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5">
                    Change
                  </button>
                  <button onClick={() => onReject(d.id)} className="text-xs text-red-400/70 hover:text-red-400 border border-red-500/20 rounded-full px-2.5 py-0.5">
                    Reject
                  </button>
                </>
              )}

              {(d.match_status === "rejected" || d.match_status === "not_applicable") && (
                <>
                  <DisclosureStatusBadge status={d.match_status} />
                  <button onClick={() => setOpenMatchId(openMatchId === d.id ? null : d.id)} className="text-xs text-gold-400 hover:text-gold-300 border border-gold-500/20 rounded-full px-2.5 py-0.5">
                    Match
                  </button>
                </>
              )}

              {d.match_status === "confirmed" && (
                <>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <p className="text-white/70 text-xs">{d.matched_company_name}</p>
                      <DisclosureStatusBadge status={d.match_status} />
                    </div>
                    {d.match_source === "vendor" && (
                      <p className="text-blue-400/70 text-[11px]">↻ Reused from vendor matching — worth a second look</p>
                    )}
                    {d.confirmed_by_email && <p className="text-white/20 text-[11px]">confirmed by {d.confirmed_by_email}</p>}
                  </div>
                  <button onClick={() => onUndoConfirm(d.id)} className="text-xs text-amber-400/70 hover:text-amber-400 border border-amber-500/20 rounded-full px-2.5 py-0.5">
                    Unconfirm
                  </button>
                  <button onClick={() => setOpenMatchId(openMatchId === d.id ? null : d.id)} className="text-xs text-white/30 hover:text-white/60">
                    Change
                  </button>
                </>
              )}
            </div>
          </div>

          {openMatchId === d.id && (
            <CompanySearchBox
              userId={userId}
              getToken={getToken}
              onAssign={(companyId, companyName) => { onConfirm(d.id, companyId, companyName); setOpenMatchId(null); }}
              onCancel={() => setOpenMatchId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function CoiProvidersPage() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();

  const [lists, setLists] = useState([]);
  const [listsError, setListsError] = useState(null);
  const [activeListId, setActiveListId] = useState(null);
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingSummary, setMatchingSummary] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);

  const [activeTab, setActiveTab] = useState("providers");
  const [disclosures, setDisclosures] = useState([]);
  const [disclosuresTotal, setDisclosuresTotal] = useState(0);
  const [disclosuresPage, setDisclosuresPage] = useState(0);
  const [disclosuresSearch, setDisclosuresSearch] = useState("");
  const [disclosuresStatusFilter, setDisclosuresStatusFilter] = useState("all");
  const [disclosuresLoading, setDisclosuresLoading] = useState(false);
  const [companyMatchingLoading, setCompanyMatchingLoading] = useState(false);
  const [companyMatchingSummary, setCompanyMatchingSummary] = useState(null);
  const [disclosureActionError, setDisclosureActionError] = useState(null);

  const refreshCoiLists = useCallback(async (selectListId) => {
    if (!user) return;
    try {
      const res = await authedFetch(getToken, `/api/user-data?action=getCoiLists&userId=${user.id}`);
      const data = await res.json();
      if (!res.ok) {
        setListsError(data.error || "Failed to load COI lists.");
        setLists([]);
        return;
      }
      setListsError(null);
      setLists(data.lists || []);
      if (selectListId) setActiveListId(selectListId);
      else if (!activeListId && data.lists?.length) setActiveListId(data.lists[0].id);
    } catch {
      setListsError("Network error loading COI lists.");
    }
  }, [user?.id]);

  useEffect(() => { refreshCoiLists(); }, [refreshCoiLists]);

  const loadProviders = useCallback(async (listId, pageArg, searchArg, statusArg) => {
    if (!listId || !user) return;
    // eslint-disable-next-line
    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: "getCoiProviders", userId: user.id, listId,
        page: String(pageArg), pageSize: String(PAGE_SIZE), search: searchArg, statusFilter: statusArg,
      });
      const res = await authedFetch(getToken, `/api/user-data?${params}`);
      const data = await res.json();
      setProviders(data.providers || []);
      setTotal(data.total || 0);
      setPage(pageArg);
    } catch {
      setProviders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeListId) loadProviders(activeListId, 0, "", "all");
  }, [activeListId, loadProviders]);

  useEffect(() => {
    if (!activeListId) return;
    const t = setTimeout(() => loadProviders(activeListId, 0, search, statusFilter), 300);
    return () => clearTimeout(t);
  }, [search, statusFilter]);

  async function handleRunMatching() {
    if (!activeListId) return;
    setMatchingLoading(true);
    setMatchingSummary(null);
    try {
      const res = await authedFetch(getToken, "/api/match-providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "runMatching", userId: user.id, listId: activeListId }),
      });
      const data = await res.json();
      setMatchingSummary(res.ok ? data : { error: data.error || "Matching failed." });
      await loadProviders(activeListId, page, search, statusFilter);
    } catch {
      setMatchingSummary({ error: "Network error. Please try again." });
    } finally {
      setMatchingLoading(false);
    }
  }

  function applyLocalStatus(providerRowId, newStatus, fields) {
    if (statusFilter !== "all" && statusFilter !== newStatus) {
      setProviders((prev) => prev.filter((p) => p.id !== providerRowId));
      return;
    }
    setProviders((prev) => prev.map((p) => (p.id === providerRowId ? { ...p, npi_match_status: newStatus, ...fields } : p)));
  }

  async function handleConfirm(providerRowId, npi) {
    const res = await authedFetch(getToken, "/api/match-providers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "confirmMatch", userId: user.id, providerRowId, npi }),
    });
    if (!res.ok) return;
    applyLocalStatus(providerRowId, "confirmed_by_user", {
      matched_npi: npi, confirmed_by_email: user.primaryEmailAddress?.emailAddress || null,
    });
  }

  async function handleReject(providerRowId) {
    const res = await authedFetch(getToken, "/api/match-providers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "rejectMatch", userId: user.id, providerRowId }),
    });
    if (!res.ok) return;
    applyLocalStatus(providerRowId, "rejected", { matched_npi: null, matched_provider_name: null, ambiguous_candidates: null });
  }

  const loadDisclosures = useCallback(async (listId, pageArg, searchArg, statusArg) => {
    if (!listId || !user) return;
    setDisclosuresLoading(true);
    try {
      const params = new URLSearchParams({
        action: "getCoiDisclosures", userId: user.id, listId,
        page: String(pageArg), pageSize: String(PAGE_SIZE), search: searchArg, statusFilter: statusArg,
      });
      const res = await authedFetch(getToken, `/api/user-data?${params}`);
      const data = await res.json();
      setDisclosures(data.disclosures || []);
      setDisclosuresTotal(data.total || 0);
      setDisclosuresPage(pageArg);
    } catch {
      setDisclosures([]);
      setDisclosuresTotal(0);
    } finally {
      setDisclosuresLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeListId && activeTab === "disclosures") loadDisclosures(activeListId, 0, "", "all");
  }, [activeListId, activeTab, loadDisclosures]);

  useEffect(() => {
    if (!activeListId || activeTab !== "disclosures") return;
    const t = setTimeout(() => loadDisclosures(activeListId, 0, disclosuresSearch, disclosuresStatusFilter), 300);
    return () => clearTimeout(t);
  }, [disclosuresSearch, disclosuresStatusFilter]);

  async function handleRunCompanyMatching() {
    if (!activeListId) return;
    setCompanyMatchingLoading(true);
    setCompanyMatchingSummary(null);
    try {
      const res = await authedFetch(getToken, "/api/match-coi-disclosures", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "runMatching", userId: user.id, listId: activeListId }),
      });
      const data = await res.json();
      setCompanyMatchingSummary(res.ok ? data : { error: data.error || "Matching failed." });
      await loadDisclosures(activeListId, disclosuresPage, disclosuresSearch, disclosuresStatusFilter);
    } catch {
      setCompanyMatchingSummary({ error: "Network error. Please try again." });
    } finally {
      setCompanyMatchingLoading(false);
    }
  }

  function applyLocalDisclosureStatus(disclosureId, newStatus, fields) {
    if (disclosuresStatusFilter !== "all" && disclosuresStatusFilter !== newStatus) {
      setDisclosures((prev) => prev.filter((d) => d.id !== disclosureId));
      return;
    }
    setDisclosures((prev) => prev.map((d) => (d.id === disclosureId ? { ...d, match_status: newStatus, ...fields } : d)));
  }

  // Shared two-phase flow: call without confirmSweep first; if the backend
  // reports other rows would also change, ask before proceeding; a plain
  // window.confirm matches the pattern already used elsewhere on this page
  // (deleteList, handleGenerateConflictReport) for this exact kind of
  // "warn before a wider-than-expected change" moment — only shown when
  // there's genuinely something else to flag, never on every click.
  async function runSweepableAction(actionName, body, onDone) {
    setDisclosureActionError(null);
    const call = async (extra) => {
      const res = await authedFetch(getToken, "/api/match-coi-disclosures", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: actionName, userId: user.id, ...body, ...extra }),
      });
      return { res, data: await res.json() };
    };

    let { res, data } = await call({});
    if (res.ok && data.needsSweepConfirmation) {
      const names = data.providerNames.slice(0, 5).join(", ") + (data.providerNames.length > 5 ? `, and ${data.providerNames.length - 5} more` : "");
      const proceed = window.confirm(
        `"${data.companyName}" also appears on ${data.affectedCount} other disclosure${data.affectedCount !== 1 ? "s" : ""} (${names}). This will update ${data.affectedCount !== 1 ? "all of them" : "it"} too, since they're the same company. Continue?`
      );
      if (!proceed) return;
      ({ res, data } = await call({ confirmSweep: true }));
    }
    if (!res.ok) { setDisclosureActionError(data.error || `${actionName} failed.`); return; }
    onDone(data);
  }

  async function handleConfirmDisclosure(disclosureId, companyId, companyName) {
    await runSweepableAction("confirmMatch", { disclosureId, companyId, companyName }, async (data) => {
      if (data.sweptCount > 0) {
        await loadDisclosures(activeListId, disclosuresPage, disclosuresSearch, disclosuresStatusFilter);
      } else {
        applyLocalDisclosureStatus(disclosureId, "confirmed", {
          matched_company_id: companyId, matched_company_name: companyName, match_source: null,
          confirmed_by_email: user.primaryEmailAddress?.emailAddress || null,
        });
      }
    });
  }

  async function handleRejectDisclosure(disclosureId) {
    await runSweepableAction("rejectMatch", { disclosureId }, async (data) => {
      if (data.sweptCount > 0) {
        await loadDisclosures(activeListId, disclosuresPage, disclosuresSearch, disclosuresStatusFilter);
      } else {
        applyLocalDisclosureStatus(disclosureId, "rejected", { matched_company_id: null, matched_company_name: null });
      }
    });
  }

  async function handleMarkNotApplicable(disclosureId) {
    await runSweepableAction("markNotApplicable", { disclosureId }, async (data) => {
      if (data.sweptCount > 0) {
        await loadDisclosures(activeListId, disclosuresPage, disclosuresSearch, disclosuresStatusFilter);
      } else {
        applyLocalDisclosureStatus(disclosureId, "not_applicable", { matched_company_id: null, matched_company_name: null });
      }
    });
  }

  async function handleUndoConfirmDisclosure(disclosureId) {
    setDisclosureActionError(null);
    const res = await authedFetch(getToken, "/api/match-coi-disclosures", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "undoConfirm", userId: user.id, disclosureId }),
    });
    const data = await res.json();
    if (!res.ok) { setDisclosureActionError(data.error || "Unconfirm failed."); return; }
    applyLocalDisclosureStatus(disclosureId, "unmatched", { matched_company_id: null, matched_company_name: null, match_source: null });
  }

  if (!isSignedIn) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">Sign in to review COI questionnaire matching.</p>
        </div>
      </main>
    );
  }

  if (!isEnterprise) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <span className="text-4xl mb-4 block">🔒</span>
          <p className="text-white font-display text-xl mb-2">COI Provider Matching</p>
          <p className="text-white/50 text-sm max-w-sm mx-auto mb-6">
            Compare provider-submitted conflict of interest disclosures against CMS Open Payments and your confirmed vendors.
          </p>
          <button
            onClick={() => {
              setUpgradeMessage("COI questionnaire matching is an Enterprise feature.");
              setShowUpgradeModal(true);
            }}
            className="btn-primary px-6 py-2.5"
          >
            Upgrade to Enterprise
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl text-white">COI Provider Matching</h1>
        {lists.length > 0 && (
          <select
            value={activeListId || ""}
            onChange={(e) => setActiveListId(e.target.value)}
            className="bg-navy-700 border border-white/10 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-white/30"
          >
            {lists.map((l) => (
              <option key={l.id} value={l.id}>{l.list_name} ({l.provider_count})</option>
            ))}
          </select>
        )}
        </div>
        <p className="text-white/40 text-xs mt-1">
          Resolving who each disclosure is about (against NPPES) and which company it names (against CMS Open Payments), so disclosed conflicts can be checked against what CMS actually reports.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowThresholds(true)}
          className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg border border-gold-500/20 text-gold-400/70 hover:text-gold-400 hover:border-gold-500/40 transition-all"
        >
          ⚙ Disclosure Thresholds
        </button>
      </div>
      {showThresholds && (
        <CoiDisclosureThresholdsPopup userId={user?.id} getToken={getToken} onClose={() => setShowThresholds(false)} />
      )}

      <CoiUploadPanel userId={user?.id} getToken={getToken} onUploaded={(listId) => refreshCoiLists(listId)} />

      {activeListId && (
        <div className="flex gap-2 border-b border-white/10">
          {[["providers", "Providers"], ["disclosures", "Company Disclosures"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`text-sm px-4 py-2 border-b-2 transition-colors ${
                activeTab === key ? "border-gold-500 text-white" : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {listsError && (
        <div className="card text-center py-12 border-red-500/20">
          <p className="text-red-400 text-sm">{listsError}</p>
        </div>
      )}
      {!listsError && !lists.length && (
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">No COI lists uploaded yet.</p>
        </div>
      )}

      {activeListId && activeTab === "providers" && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button
              onClick={handleRunMatching}
              disabled={matchingLoading}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
            >
              {matchingLoading ? "Running…" : "Run Matching"}
            </button>
            {matchingSummary && !matchingSummary.error && (
              <p className="text-white/40 text-xs">
                {matchingSummary.confirmed_by_npi || 0} by NPI · {matchingSummary.confirmed_by_user || 0} reused ·{" "}
                {matchingSummary.suggested || 0} suggested · {matchingSummary.needs_review || 0} need review ·{" "}
                {matchingSummary.unmatched || 0} unmatched
              </p>
            )}
            {matchingSummary?.error && <p className="text-red-400 text-xs">{matchingSummary.error}</p>}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or NPI..."
              className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-navy-700 border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-white/30"
            >
              <option value="all">All statuses</option>
              <option value="needs_review">Needs Review</option>
              <option value="ambiguous">Ambiguous</option>
              <option value="suggested">Suggested</option>
              <option value="unmatched">Unmatched</option>
              <option value="confirmed_by_npi">Confirmed (NPI)</option>
              <option value="confirmed_by_user">Confirmed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {loading ? (
            <div className="text-white/30 text-sm animate-pulse py-6 text-center">Loading providers…</div>
          ) : providers.length > 0 ? (
            <>
              <div className="flex items-center justify-between px-1 text-[11px] text-white/30 uppercase tracking-wide">
                <span>As submitted</span>
                <span>Resolved match</span>
              </div>
              <ProviderTable providers={providers} onConfirm={handleConfirm} onReject={handleReject} getToken={getToken} userId={user.id} listId={activeListId} />
              {total > PAGE_SIZE && (
                <PaginationControls
                  page={page} total={total} pageSize={PAGE_SIZE}
                  onPageChange={(p) => loadProviders(activeListId, p, search, statusFilter)}
                />
              )}
            </>
          ) : (
            <p className="text-white/30 text-sm text-center py-6">No providers match your filters.</p>
          )}
        </>
      )}

      {activeListId && activeTab === "disclosures" && (
        <>
          <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg px-3 py-2.5 text-xs text-white/50 leading-relaxed">
            This tab matches each disclosed company to CMS Open Payments' own tracked list of manufacturers and GPOs.
            <span className="text-green-400/80"> Confirmed</span> means this disclosure is linked to a specific CMS Open Payments entity — the provider's actual reported payments from that company can now be checked against what they disclosed.
            <span className="text-white/60"> Not Applicable</span> means this company isn't one CMS tracks (a private practice, a hospital, a non-industry entity) — there's no CMS comparison to make for this line.
            Getting this right for every distinct company name is what determines whether a disclosure gap can be flagged accurately later.
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <button
              onClick={handleRunCompanyMatching}
              disabled={companyMatchingLoading}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
            >
              {companyMatchingLoading ? "Running…" : "Run Company Matching"}
            </button>
            {companyMatchingSummary && !companyMatchingSummary.error && (
              <p className="text-white/40 text-xs">
                {companyMatchingSummary.confirmed || 0} confirmed · {companyMatchingSummary.suggested || 0} suggested ·{" "}
                {companyMatchingSummary.ambiguous || 0} ambiguous · {companyMatchingSummary.not_applicable || 0} not applicable ·{" "}
                {companyMatchingSummary.rejected || 0} rejected · {companyMatchingSummary.unmatched || 0} unmatched
              </p>
            )}
            {companyMatchingSummary?.error && <p className="text-red-400 text-xs">{companyMatchingSummary.error}</p>}
          </div>

          {disclosureActionError && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">✗ {disclosureActionError}</p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={disclosuresSearch}
              onChange={(e) => setDisclosuresSearch(e.target.value)}
              placeholder="Search company or provider..."
              className="flex-1 min-w-[200px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-gold-500/50"
            />
            <select
              value={disclosuresStatusFilter}
              onChange={(e) => setDisclosuresStatusFilter(e.target.value)}
              className="bg-navy-700 border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-white/30"
            >
              <option value="all">All statuses</option>
              <option value="ambiguous">Ambiguous</option>
              <option value="suggested">Suggested</option>
              <option value="unmatched">Unmatched</option>
              <option value="confirmed">Confirmed</option>
              <option value="not_applicable">Not Applicable</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {disclosuresLoading ? (
            <div className="text-white/30 text-sm animate-pulse py-6 text-center">Loading disclosures…</div>
          ) : disclosures.length > 0 ? (
            <>
              <div className="flex items-center justify-between px-1 text-[11px] text-white/30 uppercase tracking-wide">
                <span>As submitted</span>
                <span>Resolved match</span>
              </div>
              <DisclosureTable
                disclosures={disclosures}
                onConfirm={handleConfirmDisclosure}
                onReject={handleRejectDisclosure}
                onNotApplicable={handleMarkNotApplicable}
                onUndoConfirm={handleUndoConfirmDisclosure}
                getToken={getToken}
                userId={user.id}
              />
              {disclosuresTotal > PAGE_SIZE && (
                <PaginationControls
                  page={disclosuresPage} total={disclosuresTotal} pageSize={PAGE_SIZE}
                  onPageChange={(p) => loadDisclosures(activeListId, p, disclosuresSearch, disclosuresStatusFilter)}
                />
              )}
            </>
          ) : (
            <p className="text-white/30 text-sm text-center py-6">No disclosures match your filters.</p>
          )}
        </>
      )}
    </main>
  );
}